import { createSlice } from "@reduxjs/toolkit";
import { getProductDisplayName } from "../utils/productTitle";
import { updateCart } from "../utils/cartUtils";

// ---------- helpers ----------
const readJSON = (k, fallback) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

// --- Province helpers (CA) ---
const CA_PROVINCES = {
  ALBERTA: "AB",
  "BRITISH COLUMBIA": "BC",
  MANITOBA: "MB",
  "NEW BRUNSWICK": "NB",
  "NEWFOUNDLAND AND LABRADOR": "NL",
  "NOVA SCOTIA": "NS",
  ONTARIO: "ON",
  "PRINCE EDWARD ISLAND": "PE",
  QUEBEC: "QC",
  SASKATCHEWAN: "SK",
  "NORTHWEST TERRITORIES": "NT",
  NUNAVUT: "NU",
  YUKON: "YT",
};

function normalizeCAProvince(v) {
  if (!v) return undefined;
  const s = String(v).trim();
  if (s.length === 2) return s.toUpperCase(); // already a code
  return CA_PROVINCES[s.toUpperCase()];
}

function inferProvinceFromPostal(postal) {
  if (!postal) return undefined;
  const map = {
    A: "NL",
    B: "NS",
    C: "PE",
    E: "NB",
    G: "QC",
    H: "QC",
    J: "QC",
    K: "ON",
    L: "ON",
    M: "ON",
    N: "ON",
    P: "ON",
    R: "MB",
    S: "SK",
    T: "AB",
    V: "BC",
    X: "NT",
    Y: "YT",
  };
  const first = postal.trim().toUpperCase().replace(/\s+/g, "")[0];
  return map[first];
}

function normalizeShippingAddress(addr = {}) {
  const country = (addr.country || "CA").toUpperCase();

  const cleaned = {
    name: addr.name || "",
    address: addr.address || addr.address1 || "",
    address2: addr.address2 || "",
    city: addr.city || "",
    postalCode: (addr.postalCode || addr.zip || "")
      .toString()
      .toUpperCase(),
    country,
    phone: addr.phone || "",
    province: addr.province || addr.state || "",
    state: addr.state || "",
  };

  if (cleaned.postalCode) cleaned.postalCode = cleaned.postalCode.replace(/\s+/g, "");

  if (["CA", "US"].includes(country)) {
    let prov = cleaned.province || cleaned.state;
    if (country === "CA") {
      prov =
        normalizeCAProvince(prov) ||
        inferProvinceFromPostal(cleaned.postalCode) ||
        prov ||
        "ON";
    }
    cleaned.province = prov || cleaned.province || cleaned.state || "";
  }

  return cleaned;
}

// Persist the whole cart snapshot (and let updateCart compute totals)
const persist = (state) => {
  const next = updateCart(state);

  try {
    localStorage.setItem(
      "cart",
      JSON.stringify({
        cartItems: next.cartItems || [],
        shippingAddress: next.shippingAddress || {},
        paymentMethod: next.paymentMethod || "PayPal",
        guestMode: !!next.guestMode,
        guestInfo: next.guestInfo || {},
        ownerUserId: next.ownerUserId || null,
        fulfillmentMethod: next.fulfillmentMethod || "ship",
        pickupLocation: next.pickupLocation || null,
        itemsPrice: next.itemsPrice,
        shippingPrice: next.shippingPrice,
        taxPrice: next.taxPrice,
        totalPrice: next.totalPrice,
      })
    );
  } catch {}

  try {
    if (next.guestMode) {
      localStorage.setItem("guestInfo", JSON.stringify(next.guestInfo || {}));
    } else {
      localStorage.setItem("guestInfo", JSON.stringify({}));
    }
  } catch {}

  return next;
};

// ---------- initial state ----------
const fromCart = readJSON("cart", null);
const fromGuest = readJSON("guestInfo", {});

const initialState = fromCart
  ? {
      cartItems: fromCart.cartItems || [],
      shippingAddress: normalizeShippingAddress(fromCart.shippingAddress || {}),
      paymentMethod: fromCart.paymentMethod || "PayPal",
      guestMode: !!fromCart.guestMode,
      guestInfo: Object.keys(fromGuest).length
        ? fromGuest
        : fromCart.guestInfo || {},
      ownerUserId: fromCart.ownerUserId || null,
      fulfillmentMethod: fromCart.fulfillmentMethod || "ship",
      pickupLocation: fromCart.pickupLocation || null,
      itemsPrice: fromCart.itemsPrice,
      shippingPrice: fromCart.shippingPrice,
      taxPrice: fromCart.taxPrice,
      totalPrice: fromCart.totalPrice,
    }
  : {
      cartItems: [],
      shippingAddress: {},
      paymentMethod: "PayPal",
      guestMode: false,
      guestInfo: {},
      ownerUserId: null,
      fulfillmentMethod: "ship",
      pickupLocation: null,
      itemsPrice: 0,
      shippingPrice: 0,
      taxPrice: 0,
      totalPrice: 0,
    };

// ---------- slice ----------
const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const LIMIT = 10;

      const raw = action.payload || {};
      if (raw.status && raw.status !== 'active') return;
      const replaceQty = !!raw.replaceQty;
      const isTieredItem = Boolean(raw.ignoreSale);

      const item = {
        ...raw,
        product: raw.product || raw._id, // required by backend
        price: Number(raw.price ?? 0),
        salePrice: Number(raw.salePrice ?? 0),
        saleStartAt: raw.saleStartAt ?? null,
        saleEndAt: raw.saleEndAt ?? null,
        tierPrice: raw.tierPrice ?? null,
        tierPriceApplied: Boolean(raw.tierPriceApplied),
        ignoreSale: Boolean(raw.ignoreSale),
        displayName:
          raw.displayName ||
          getProductDisplayName(raw) ||
          raw.name,
        name:
          raw.name ||
          raw.displayName ||
          getProductDisplayName(raw),
        qty: Number(raw.qty ?? 1),
      };

      const key = (x) => x._id || x.product;
      const idx = state.cartItems.findIndex((x) => key(x) === key(item));

      // Helper: clamp qty to LIMIT and (optional) stock
      const clampQty = (q, stock) => {
        let next = Number.isFinite(q) ? q : 1;
        const minQty = 1;
        const maxQty = isTieredItem ? Number.POSITIVE_INFINITY : LIMIT;
        next = Math.max(minQty, next);
        if (Number.isFinite(maxQty)) {
          next = Math.min(next, maxQty);
        }
        if (stock > 0) next = Math.min(next, stock);
        return next;
      };

      if (idx >= 0) {
        const prev = state.cartItems[idx] || {};
        const prevQty = Number(prev.qty ?? 0);
        const incomingQty = Number(item.qty ?? 1);

        // increment by default; replace only when replaceQty=true (CartScreen)
        let nextQty = replaceQty ? incomingQty : prevQty + incomingQty;

        const stock = Number(item.stockQty ?? prev.stockQty ?? 0);
        nextQty = clampQty(nextQty, stock);

        const merged = {
          ...prev,
          ...item,
          qty: nextQty,
        };

        // If new payload forgot salePrice, keep previous salePrice
        if (!(Number(item.salePrice) > 0) && Number(prev.salePrice) > 0) {
          merged.salePrice = Number(prev.salePrice);
        }
        if (!item.saleStartAt && prev.saleStartAt) {
          merged.saleStartAt = prev.saleStartAt;
        }
        if (!item.saleEndAt && prev.saleEndAt) {
          merged.saleEndAt = prev.saleEndAt;
        }
        if (!item.tierPriceApplied && prev.tierPriceApplied) {
          merged.tierPriceApplied = prev.tierPriceApplied;
        }
        if (item.tierPrice == null && prev.tierPrice != null) {
          merged.tierPrice = prev.tierPrice;
        }
        if (!item.ignoreSale && prev.ignoreSale) {
          merged.ignoreSale = prev.ignoreSale;
        }

        state.cartItems[idx] = merged;
      } else {
        // New item: clamp qty too
        const stock = Number(item.stockQty ?? 0);
        const safeItem = {
          ...item,
          qty: clampQty(item.qty, stock),
        };
        state.cartItems.push(safeItem);
      }

      return persist(state);
    },

    removeFromCart: (state, action) => {
      const id = action.payload;
      state.cartItems = state.cartItems.filter(
        (x) => (x._id || x.product) !== id
      );
      return persist(state);
    },

    clearCartItems: (state) => {
      state.cartItems = [];
      return persist(state);
    },

    saveShippingAddress: (state, action) => {
      state.shippingAddress = normalizeShippingAddress(action.payload || {});
      return persist(state);
    },

    savePaymentMethod: (state, action) => {
      state.paymentMethod = action.payload || "PayPal";
      return persist(state);
    },

    saveGuestInfo: (state, action) => {
      const { name = "", email = "", phone = "" } = action.payload || {};
      state.guestInfo = { name, email, phone };
      return persist(state);
    },

    setCartOwner: (state, action) => {
      const newOwner = action.payload || null;
      if (state.ownerUserId !== newOwner) {
        state.ownerUserId = newOwner;
        state.guestMode = newOwner === "guest";
        state.guestInfo = {};
        state.shippingAddress = {};
        state.fulfillmentMethod = "ship";
        state.pickupLocation = null;
        try {
          localStorage.setItem("guestInfo", JSON.stringify({}));
        } catch {}
      }
      return persist(state);
    },

    setFulfillmentMethod: (state, action) => {
      const payload = action.payload;
      const method =
        typeof payload === "string"
          ? payload
          : payload?.method || state.fulfillmentMethod || "ship";

      state.fulfillmentMethod = method === "pickup" ? "pickup" : "ship";

      if (typeof payload === "object" && "pickupLocation" in payload) {
        state.pickupLocation = payload.pickupLocation || null;
      } else if (state.fulfillmentMethod !== "pickup") {
        state.pickupLocation = null;
      }

      if (state.fulfillmentMethod === "pickup") {
        state.shippingAddress = {};
      } else {
        state.pickupLocation = null;
      }
      return persist(state);
    },

    setPickupLocation: (state, action) => {
      state.pickupLocation = action.payload || null;
      if (state.pickupLocation && state.fulfillmentMethod !== "pickup") {
        state.fulfillmentMethod = "pickup";
        state.shippingAddress = {};
      }
      return persist(state);
    },

    startGuestCheckout: (state) => {
      state.guestMode = true;
      state.ownerUserId = "guest";
      state.shippingAddress = {};
      state.guestInfo = {};
      state.fulfillmentMethod = "ship";
      state.pickupLocation = null;
      return persist(state);
    },

    resetCartState: () => {
      const fresh = {
        cartItems: [],
        shippingAddress: {},
        paymentMethod: "PayPal",
        guestMode: false,
        guestInfo: {},
        ownerUserId: null,
        fulfillmentMethod: "ship",
        pickupLocation: null,
        itemsPrice: 0,
        shippingPrice: 0,
        taxPrice: 0,
        totalPrice: 0,
      };
      persist(fresh);
      return fresh;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  clearCartItems,
  saveShippingAddress,
  savePaymentMethod,
  saveGuestInfo,
  setCartOwner,
  setFulfillmentMethod,
  setPickupLocation,
  startGuestCheckout,
  resetCartState,
} = cartSlice.actions;

export default cartSlice.reducer;
